/**
 * How a case's input and expectations are shown. Each suite has its own shapes, so each
 * gets its own view; a suite without one is shown as JSON.
 */
import type { FC, ReactNode } from 'react';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const text = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const JsonView = ({ value }: { value: unknown }) => (
  <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap">
    {JSON.stringify(value, null, 2)}
  </pre>
);

/** summarize-chat input: `{ conversation: ConversationItem[] }`, as the app stores chats. */
const ConversationView = ({ input }: { input: unknown }) => {
  if (!isRecord(input) || !Array.isArray(input.conversation)) {
    return <JsonView value={input} />;
  }

  return (
    <ol className="space-y-2">
      {input.conversation.filter(isRecord).map((item, index) => {
        const key = text(item.id) ?? String(index);
        if (item.type === 'message') {
          const isUser = item.role === 'user';
          return (
            <li key={key} className={`text-sm rounded-lg px-3 py-2 ${isUser ? 'bg-blue-50 ml-8' : 'bg-gray-100 mr-8'}`}>
              <span className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500">{isUser ? 'User' : 'Ollie'}</span>
              <span className="whitespace-pre-wrap">{text(item.displayContent) ?? text(item.content)}</span>
            </li>
          );
        }
        if (item.type === 'tool_call') {
          return (
            <li key={key} className="text-xs text-gray-500 font-mono mr-8">
              tool call: {text(item.name)}({text(item.arguments)})
            </li>
          );
        }
        if (item.type === 'tool_result') {
          return (
            <li key={key} className="text-xs text-gray-500 font-mono mr-8">
              tool result: {text(item.tool_name)} → {text(item.result)}
            </li>
          );
        }
        return (
          <li key={key}>
            <JsonView value={item} />
          </li>
        );
      })}
    </ol>
  );
};

const FactList = ({ title, items, tone }: { title: string; items: string[]; tone: 'normal' | 'risk' | 'wrong' }) => {
  if (items.length === 0) return null;
  const toneClass = { normal: 'text-gray-800', risk: 'text-red-800', wrong: 'text-gray-700' }[tone];
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{title}</h4>
      <ol className={`list-decimal list-inside text-sm space-y-0.5 ${toneClass}`}>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ol>
    </div>
  );
};

/** summarize-chat expectations: `{ mustInclude, mustKeepRisk?, mustNotInclude? }`. */
const SummaryExpectView = ({ expect }: { expect: unknown }) => {
  if (!isRecord(expect)) {
    return <JsonView value={expect} />;
  }
  return (
    <div className="space-y-3">
      <FactList title="Must include" items={strings(expect.mustInclude)} tone="normal" />
      <FactList title="Must keep (risk)" items={strings(expect.mustKeepRisk)} tone="risk" />
      <FactList title="Must not say" items={strings(expect.mustNotInclude)} tone="wrong" />
    </div>
  );
};

const Detail = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="text-xs">
    <span className="font-semibold uppercase tracking-wide text-gray-600">{label}</span> <span className="text-gray-900">{children}</span>
  </div>
);

/** ollie-chat input: the context the app sends, then the chat that ends with the user's message. */
const OllieChatInputView = ({ input }: { input: unknown }) => {
  if (!isRecord(input)) {
    return <JsonView value={input} />;
  }
  const user = isRecord(input.user) ? input.user : {};
  const userParts = [
    user.isPro === false ? 'free' : 'pro',
    typeof user.memberForDays === 'number' ? `member for ${user.memberForDays} days` : null,
    typeof user.questDay === 'number' ? `quest day ${user.questDay}${user.questDayCompleted ? ' (done)' : ''}` : null,
  ].filter(Boolean);
  const memory = Array.isArray(input.chatMemory) ? input.chatMemory.filter(isRecord) : [];
  const reference = isRecord(input.chatReference) ? input.chatReference : null;

  return (
    <div className="space-y-3">
      <div className="space-y-1 rounded-lg border border-gray-200 p-2">
        <Detail label="Chat type">{text(input.chatType) ?? 'general'}</Detail>
        <Detail label="User">{userParts.join(', ')}</Detail>
        {memory.length > 0 && <Detail label="Past chats">{memory.map((entry) => text(entry.title)).join('; ')}</Detail>}
        {reference && <Detail label="Refers to">{text(reference.title)}</Detail>}
        {isRecord(input.ocdProfile) && (
          <details className="text-xs">
            <summary className="cursor-pointer font-semibold uppercase tracking-wide text-gray-600">OCD profile</summary>
            <JsonView value={input.ocdProfile} />
          </details>
        )}
      </div>
      <ConversationView input={input} />
    </div>
  );
};

const ToolList = ({ title, names, tone }: { title: string; names: string[]; tone: string }) =>
  names.length === 0 ? null : (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{title}</h4>
      <div className="flex flex-wrap gap-1">
        {names.map((name) => (
          <span key={name} className={`rounded-full border px-2 py-0.5 text-xs font-mono ${tone}`}>
            {name}
          </span>
        ))}
      </div>
    </div>
  );

/** ollie-chat expectations: `{ mustDo, mustNotDo?, tools?: { required?, forbidden? } }`. */
const OllieChatExpectView = ({ expect }: { expect: unknown }) => {
  if (!isRecord(expect)) {
    return <JsonView value={expect} />;
  }
  const tools = isRecord(expect.tools) ? expect.tools : {};
  return (
    <div className="space-y-3">
      <FactList title="Must do" items={strings(expect.mustDo)} tone="normal" />
      <FactList title="Must not do" items={strings(expect.mustNotDo)} tone="wrong" />
      <ToolList title="Tools required" names={strings(tools.required)} tone="border-green-300 bg-green-50 text-green-900" />
      <ToolList title="Tools forbidden" names={strings(tools.forbidden)} tone="border-red-300 bg-red-50 text-red-900" />
    </div>
  );
};

const VIEWS: Record<string, { Input: FC<{ input: unknown }>; Expect: FC<{ expect: unknown }> }> = {
  'summarize-chat': { Input: ConversationView, Expect: SummaryExpectView },
  'ollie-chat': { Input: OllieChatInputView, Expect: OllieChatExpectView },
};

export const CaseInputView = ({ suiteId, input }: { suiteId: string; input: unknown }) => {
  const View = VIEWS[suiteId]?.Input;
  return View ? <View input={input} /> : <JsonView value={input} />;
};

export const CaseExpectView = ({ suiteId, expect }: { suiteId: string; expect: unknown }) => {
  const View = VIEWS[suiteId]?.Expect;
  return View ? <View expect={expect} /> : <JsonView value={expect} />;
};
